<template>
    <div class="top-bar" tabindex="-1">
        <RouterLink to="/" class="top-bar-logo" @click="closeMobileMenu">
            <IconItem icon="mdi:leaf" class="top-bar-logo-icon" height="1.35em" />
            <span class="top-bar-logo-text"> <span class="top-bar-logo-accent">Terra</span>Scribe </span>
        </RouterLink>

        <nav class="top-bar-nav" :class="{ 'mobile-open': mobileMenuOpen }">
            <RouterLink to="/" class="top-bar-nav-item" :class="{ active: $route.name === 'home' }" @click="closeMobileMenu">
                <IconItem icon="mdi:home" height="1.1em" />
                <span>{{ $t("Home") }}</span>
            </RouterLink>
            <RouterLink
                v-if="isOperator"
                to="/templates"
                class="top-bar-nav-item"
                :class="{ active: $route.name === 'templates' || $route.name === 'template-wizard' || $route.name === 'template-detail' }"
                @click="closeMobileMenu"
            >
                <IconItem icon="mdi:format-list-bulleted" height="1.1em" />
                <span>{{ $t("Templates") }}</span>
            </RouterLink>
            <RouterLink
                to="/projects"
                class="top-bar-nav-item"
                :class="{ active: $route.name === 'projects' || $route.name === 'project-new' }"
                @click="closeMobileMenu"
            >
                <IconItem icon="mdi:folder" height="1.1em" />
                <span>{{ $t("Projects") }}</span>
            </RouterLink>
            <RouterLink
                v-if="isOperator"
                to="/qgis"
                class="top-bar-nav-item"
                :class="{ active: $route.name === 'qgis' }"
                @click="closeMobileMenu"
            >
                <IconItem icon="mdi:table" height="1.1em" />
                <span>{{ $t("QGIS Tables") }}</span>
            </RouterLink>
            <RouterLink
                v-if="isOperator && fairEnabled"
                to="/dataspace"
                class="top-bar-nav-item"
                :class="{ active: $route.name === 'dataspace' }"
                @click="closeMobileMenu"
            >
                <IconItem icon="mdi:transit-connection-variant" height="1.1em" />
                <span>{{ $t("Dataspace") }}</span>
            </RouterLink>
            <!--<div class="top-bar-dropdown" ref="toolsDropdown">
                <button type="button" class="top-bar-nav-item" :class="{ active: isToolsRouteActive }" @click="toggleToolsDropdown">
                    <IconItem icon="mdi:toolbox" height="1.1em" />
                    <span>{{ $t("Tools") }}</span>
                    <IconItem
                        icon="mdi:chevron-down"
                        height="0.8em"
                        class="top-bar-dropdown-chevron"
                        :class="{ open: toolsDropdownOpen }"
                    />
                </button>
                <Transition name="tb-dropdown">
                    <div v-if="toolsDropdownOpen" class="top-bar-dropdown-menu">
                        <RouterLink
                            to="/custom-query"
                            class="top-bar-dropdown-item"
                            :class="{ active: $route.name === 'custom-query' }"
                            @click="closeToolsDropdown"
                        >
                            <IconItem icon="mdi:database-search" height="1.1em" />
                            <span>{{ $t("GIS Query") }}</span>
                        </RouterLink>
                        <RouterLink
                            to="/geo-upload"
                            class="top-bar-dropdown-item"
                            :class="{ active: $route.name === 'geo-upload' }"
                            @click="closeToolsDropdown"
                        >
                            <IconItem icon="mdi:upload" height="1.1em" />
                            <span>{{ $t("Geo Upload") }}</span>
                        </RouterLink>
                    </div>
                </Transition>
            </div>-->
        </nav>

        <div class="top-bar-right">
            <button type="button" class="top-bar-icon-btn" :title="$t('Change theme')" @click="invertTheme">
                <IconItem v-if="theme === 'dark'" icon="mdi:weather-sunny" height="1.2em" />
                <IconItem v-else icon="mdi:weather-night" height="1.2em" />
            </button>

            <button type="button" class="top-bar-icon-btn" :title="$t('Select your language')" @click="selectLanguage">
                <IconItem icon="mdi:translate" height="1.2em" />
            </button>

            <span class="top-bar-sep"></span>

            <button v-if="loggedIn" type="button" class="top-bar-user-btn" :title="$t('User settings')" @click="openUserSettings">
                <img v-if="profileImage" class="top-bar-avatar" :src="profileImage" :alt="renderName(profileName)" />
                <img v-else class="top-bar-avatar" src="@/assets/user.png" :alt="renderName(profileName)" />
                <span class="top-bar-user-name">{{ renderName(profileName) }}</span>
                <IconItem icon="mdi:chevron-down" height="0.9em" class="top-bar-chevron" />
            </button>

            <button v-else type="button" class="top-bar-login-btn" @click="login">
                <IconItem icon="mdi:login" height="1em" />
                <span>{{ $t("Login") }}</span>
            </button>

            <button type="button" class="top-bar-hamburger" :class="{ open: mobileMenuOpen }" :title="$t('Menu')" @click="toggleMobileMenu">
                <span></span>
                <span></span>
                <span></span>
            </button>
        </div>

        <Transition name="tb-fade">
            <div v-if="mobileMenuOpen" class="top-bar-backdrop" @click="closeMobileMenu" />
        </Transition>
    </div>
</template>

<script lang="ts">
import { ColorThemeName, getTheme, setTheme } from "@/control/app-preferences";
import { AuthController } from "@/control/auth";
import { defineComponent } from "vue";

export default defineComponent({
    components: {},
    name: "TopBar",
    emits: ["openModal"],
    data: function () {
        return {
            platformName: import.meta.env.VITE__PLATFORM_NAME || "TerraScribe",
            loggedIn: AuthController.isAuthenticated(),
            isOperator: AuthController.isOperator(),
            fairEnabled: AuthController.isFairEnabled(),
            profileImage: AuthController.ProfileImage,
            theme: getTheme(),
            profileName: AuthController.ProfileName || AuthController.Username || "",
            mobileMenuOpen: false,
            toolsDropdownOpen: false,
        };
    },
    computed: {
        isToolsRouteActive(): boolean {
            return this.$route.name === "custom-query" || this.$route.name === "geo-upload";
        },
    },
    methods: {
        openUserSettings: function () {
            this.$emit("openModal", "account-settings");
        },

        selectLanguage: function () {
            this.$emit("openModal", "change-language-modal");
        },

        invertTheme: function () {
            setTheme(this.theme === "dark" ? "light" : "dark");
        },

        login: function () {
            this.$requireLogin();
        },

        toggleMobileMenu: function () {
            this.mobileMenuOpen = !this.mobileMenuOpen;
        },

        closeMobileMenu: function () {
            this.mobileMenuOpen = false;
        },

        toggleToolsDropdown: function () {
            this.toolsDropdownOpen = !this.toolsDropdownOpen;
        },

        closeToolsDropdown: function () {
            this.toolsDropdownOpen = false;
            this.closeMobileMenu();
        },

        onClickOutsideTools: function (event: MouseEvent) {
            const dropdown = this.$refs.toolsDropdown as HTMLElement | undefined;
            if (dropdown && !dropdown.contains(event.target as Node)) {
                this.toolsDropdownOpen = false;
            }
        },

        onAuthChanged: function () {
            this.loggedIn = AuthController.isAuthenticated();
            this.isOperator = AuthController.isOperator();
            this.fairEnabled = AuthController.isFairEnabled();
            this.profileName = AuthController.ProfileName || AuthController.Username || "";
            this.profileImage = AuthController.ProfileImage;
        },

        onThemeChanged: function (t: ColorThemeName) {
            this.theme = t;
        },

        renderName: function (name: string): string {
            return ((name + "").split(" ")[0] + "").split(",")[0] || "???";
        },
    },
    watch: {
        $route: function () {
            this.mobileMenuOpen = false;
            this.toolsDropdownOpen = false;
        },
    },
    mounted: function () {
        this.$listenOnAppEvent("auth-status-changed", this.onAuthChanged.bind(this));
        this.$listenOnAppEvent("theme-changed", this.onThemeChanged.bind(this));
        document.addEventListener("click", this.onClickOutsideTools);
    },
    beforeUnmount: function () {
        document.removeEventListener("click", this.onClickOutsideTools);
    },
});
</script>

<style>
@import "@/style/layout/top-bar.css";
</style>
